/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import { SideNavigation, Typography } from '@neo4j-ndl/react';
import {
  AcademicCapIconOutline,
  ChartBarIconOutline,
  CodeBracketIconOutline,
  Cog6ToothIconOutline,
  HomeIconOutline,
  MagnifyingGlassIconOutline,
  ServerIconOutline,
  WrenchScrewdriverIconOutline,
} from '@neo4j-ndl/react/icons';
import { useState } from 'react';

const Component = () => {
  const [active, setActive] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="n-h-[calc(40vh-32px)] n-min-h-[700px] n-flex n-bg-neutral-bg-default">
      <SideNavigation
        isExpanded={isExpanded}
        shouldOverlayOnInteraction={true}
        onPinButtonClick={() => setIsExpanded(!isExpanded)}
        ariaLabel="Main side navigation"
      >
        <SideNavigation.CategoryHeader>
          Example Category Header
        </SideNavigation.CategoryHeader>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<HomeIconOutline />}
            label="Nav Item"
            isActive={active === 'nav-item-1'}
            onClick={() => setActive('nav-item-1')}
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<CodeBracketIconOutline />}
            label="Nav Item"
            isActive={active === 'nav-item-2'}
            onClick={() => setActive('nav-item-2')}
          />
        </SideNavigation.ListItem>
        <SideNavigation.Divider />
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<ServerIconOutline />}
            label="Nav Item"
            isActive={active === 'nav-item-3'}
            onClick={() => setActive('nav-item-3')}
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.CategoryItem
            icon={<WrenchScrewdriverIconOutline />}
            label="Category Item"
            isActive={['nav-item-4', 'nav-item-5'].includes(active ?? '')}
          >
            <SideNavigation.ListItem>
              <SideNavigation.NavItem
                icon={<MagnifyingGlassIconOutline />}
                label="Nested Nav Item"
                isActive={active === 'nav-item-4'}
                onClick={() => setActive('nav-item-4')}
              />
            </SideNavigation.ListItem>
            <SideNavigation.ListItem>
              <SideNavigation.NavItem
                icon={<MagnifyingGlassIconOutline />}
                label="Nested Nav Item"
                isActive={active === 'nav-item-5'}
                onClick={() => setActive('nav-item-5')}
              />
            </SideNavigation.ListItem>
          </SideNavigation.CategoryItem>
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<ChartBarIconOutline />}
            label="Nav Item"
            isActive={active === 'nav-item-6'}
            onClick={() => setActive('nav-item-6')}
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<Cog6ToothIconOutline />}
            label="Nav Item"
            isActive={active === 'nav-item-7'}
            onClick={() => setActive('nav-item-7')}
          />
        </SideNavigation.ListItem>
        <SideNavigation.Divider />
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<AcademicCapIconOutline />}
            label="Nav Item"
            isActive={active === 'nav-item-8'}
            onClick={() => setActive('nav-item-8')}
          />
        </SideNavigation.ListItem>
      </SideNavigation>

      <div className="n-h-full n-w-full">
        <div className="n-flex n-flex-col n-p-8 n-w-full n-max-w-[1024px] n-mx-auto">
          <div className="n-flex n-flex-col n-gap-token-8 n-bg-neutral-bg-weak n-p-token-32 n-rounded-lg n-items-center n-justify-center">
            <Typography variant="title-4">Default Side Nav</Typography>
            <Typography variant="body-medium">
              This is the default behavior for a side nav. It expands on hover,
              and can be pinned to stay expanded.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Component;
